import { useCallback, useEffect, useRef, useState } from 'react';
import { api, postKeepalive } from '../lib/api';
import { shouldBlock } from './shortcuts';

/**
 * Keeping the exam on the screen, and recording it when it is not.
 *
 * What a browser can do here is worth stating exactly, because the difference
 * matters to a centre relying on it:
 *
 * - It can fill the screen, and see when full screen ends.
 * - It can see its window lose focus, and its tab go hidden.
 * - It can ask before the page is closed, and report that it is going.
 * - It can refuse the keystrokes the browser hands it: print, save, view
 *   source, browser find. See shortcuts.ts for which those are, and for the
 *   ones — Alt+F4, Alt+Tab, the Windows key — that no page is ever handed.
 *
 * It cannot stop any of them. No page can hold an operating system's window
 * switcher, block cmd-tab, or refuse to be closed — a browser that let a page
 * do that would be a browser nobody could get out of. So this makes leaving
 * visible and costly rather than impossible: the screen is covered the moment
 * the exam is not in front, every departure is recorded on the server, and the
 * sitting ends when it happens again. A locked-down machine is what actually
 * prevents it; this is what makes it answerable afterwards.
 */

export type BreachKind = 'HIDDEN' | 'BLUR' | 'FULLSCREEN_EXIT' | 'CLOSED';

/** The Keyboard Lock API, which TypeScript's DOM library does not yet name. */
type KeyboardLock = { lock?: (keys?: string[]) => Promise<void>; unlock?: () => void };
const keyboard = (): KeyboardLock | undefined =>
  (navigator as Navigator & { keyboard?: KeyboardLock }).keyboard;

/**
 * Full screen, with the keyboard held.
 *
 * Chrome and Edge let a page that is full screen ask for the keys the system
 * keeps for itself — Alt+Tab, the Windows key, Ctrl+W, Ctrl+T, Ctrl+N — and
 * then deliver them to the page instead of acting on them. With them held,
 * Alt+Tab does nothing, and leaving full screen takes Esc held down for two
 * seconds rather than a tap, which the browser explains on screen. Nothing is
 * installed and nothing is asked: the browser grants it with full screen.
 *
 * Where it is not offered — Safari, Firefox — this is plain full screen, and
 * the covering and counting below are what stands. Ctrl+Alt+Del, and Cmd+Tab
 * on a Mac, are never the page's to hold.
 *
 * Must be called from a click: full screen is only granted inside one.
 */
export function enterExamFullscreen(): void {
  const done = document.fullscreenElement
    ? Promise.resolve()
    : document.documentElement.requestFullscreen?.();
  void Promise.resolve(done)
    .then(() => keyboard()?.lock?.())
    .catch(() => undefined);
}

/** The keyboard back to the system, once the paper no longer needs it. */
export function releaseKeyboard(): void {
  try {
    keyboard()?.unlock?.();
  } catch {
    // Nothing held, or nothing to hold it with.
  }
}

export interface Lockdown {
  /** How many times the candidate has left the test during this sitting. */
  breaches: number;
  /** How many it takes to end the sitting. */
  limit: number;
  /** True once the sitting has been closed for leaving too often. */
  ended: boolean;
  /** False while the exam is not filling the screen. */
  fullscreen: boolean;
  /** Asks for the screen back. Must be called from a click. */
  enterFullscreen: () => void;
}

/**
 * One switch away and back can raise both `blur` and `visibilitychange`, and a
 * candidate returning to full screen raises a third. They are one departure,
 * so reports inside this window are folded together.
 */
const COALESCE_MS = 1_500;

/**
 * The first moments after a paper opens belong to the browser: a permission
 * prompt for sound, the window settling, the fullscreen request itself. None
 * of them are a candidate looking something up, and a sitting must not end
 * because of one.
 */
const SETTLE_MS = 2_000;

export function useLockdown(sittingId: string | null, active: boolean): Lockdown {
  const [breaches, setBreaches] = useState(0);
  const [limit, setLimit] = useState(2);
  const [ended, setEnded] = useState(false);
  const [fullscreen, setFullscreen] = useState(() => Boolean(document.fullscreenElement));

  const openedAt = useRef(Date.now());
  const lastReport = useRef(0);
  const endedRef = useRef(false);

  const enterFullscreen = useCallback(() => enterExamFullscreen(), []);

  const report = useCallback(
    async (kind: BreachKind) => {
      if (!sittingId || !active || endedRef.current) return;

      const now = Date.now();
      if (now - openedAt.current < SETTLE_MS) return;
      if (now - lastReport.current < COALESCE_MS) return;
      lastReport.current = now;

      // On the way out there is no response to wait for, so the report is sent
      // in the one form that survives the page.
      if (kind === 'CLOSED') {
        postKeepalive(`/my/sittings/${sittingId}/breach`, { kind });
        return;
      }

      try {
        const { data } = await api.post<{ count: number; limit: number; ended: boolean }>(
          `/my/sittings/${sittingId}/breach`,
          { kind },
        );
        setBreaches(data.count);
        setLimit(data.limit);
        if (data.ended) {
          endedRef.current = true;
          setEnded(true);
        }
      } catch {
        // A sitting that cannot be reached is already the server's business:
        // the heartbeat notices, and the paper closes on its own deadline.
      }
    },
    [active, sittingId],
  );

  useEffect(() => {
    if (!active) return;
    openedAt.current = Date.now();

    const onHidden = () => {
      if (document.hidden) void report('HIDDEN');
    };
    const onBlur = () => void report('BLUR');
    const onFullscreen = () => {
      const on = Boolean(document.fullscreenElement);
      setFullscreen(on);
      if (!on) void report('FULLSCREEN_EXIT');
    };
    /**
     * Closing or reloading. The browser decides whether to show its own
     * warning and ignores any wording we give it, so the value is the report
     * that goes with it — a paper that vanished is a paper somebody closed.
     */
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    const onPageHide = () => void report('CLOSED');

    /**
     * The keystrokes this can refuse. Captured rather than bubbled, so a
     * textarea with its own handler cannot swallow the event first — and the
     * ones a candidate legitimately uses inside the Writing answer are not in
     * the list at all.
     */
    const onKeyDown = (event: KeyboardEvent) => {
      if (shouldBlock(event, { inFullscreen: Boolean(document.fullscreenElement) })) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    /**
     * The context menu, except inside the Writing answer.
     *
     * "Search Google for…", "Open in a new tab" and "Inspect" all sit in that
     * menu. The real computer-delivered test does offer cut, copy and paste on
     * a right-click in the answer box, so that one keeps its menu.
     */
    const onContextMenu = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.tagName === 'TEXTAREA') return;
      event.preventDefault();
    };

    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('blur', onBlur);
    document.addEventListener('fullscreenchange', onFullscreen);
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('keydown', onKeyDown, { capture: true });
    window.addEventListener('contextmenu', onContextMenu);

    return () => {
      // The paper is over, or this page is: the keys are the system's again.
      releaseKeyboard();
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('fullscreenchange', onFullscreen);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('keydown', onKeyDown, { capture: true });
      window.removeEventListener('contextmenu', onContextMenu);
    };
  }, [active, report]);

  return { breaches, limit, ended, fullscreen, enterFullscreen };
}
